import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

export default function Metronome() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [timeSignature, setTimeSignature] = useState(4);
  const [beatCount, setBeatCount] = useState(0);
  const audioContextRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const scheduleIDRef = useRef(null);
  const beatCountRef = useRef(0);
  const currentBpmRef = useRef(120);
  const currentTimeSignatureRef = useRef(4);

  const LOOKAHEAD_TIME = 25; // ms
  const SCHEDULE_AHEAD_TIME = 0.1; // seconds

  useEffect(() => {
    // Initialize audio context
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    return () => {
      if (scheduleIDRef.current) {
        clearTimeout(scheduleIDRef.current);
      }
    };
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    currentBpmRef.current = bpm;
  }, [bpm]);

  useEffect(() => {
    currentTimeSignatureRef.current = timeSignature;
  }, [timeSignature]);

  const playClick = (time, currentTimeSignature) => {
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    // Different frequencies for beat and offbeats
    const isBeat = beatCountRef.current % currentTimeSignature === 0;
    osc.frequency.value = isBeat ? 1000 : 800;

    gain.gain.setValueAtTime(isMuted ? 0 : (isBeat ? 0.3 : 0.15), time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);

    osc.start(time);
    osc.stop(time + 0.1);

    beatCountRef.current = (beatCountRef.current + 1) % currentTimeSignature;
    
    // Update beat counter to trigger re-render
    setBeatCount(c => c + 1);
  };

  const nextNote = (currentBpm) => {
    const secondsPerBeat = 60.0 / currentBpm;
    nextNoteTimeRef.current += secondsPerBeat;
  };

  const scheduler = () => {
    const ctx = audioContextRef.current;
    
    while (nextNoteTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_TIME) {
      playClick(nextNoteTimeRef.current, currentTimeSignatureRef.current);
      nextNote(currentBpmRef.current);
    }

    scheduleIDRef.current = setTimeout(scheduler, LOOKAHEAD_TIME);
  };

  const handleStart = () => {
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    nextNoteTimeRef.current = ctx.currentTime;
    beatCountRef.current = 0;
    currentBpmRef.current = bpm;
    currentTimeSignatureRef.current = timeSignature;
    setIsPlaying(true);
    scheduler();
  };

  const handleStop = () => {
    setIsPlaying(false);
    if (scheduleIDRef.current) {
      clearTimeout(scheduleIDRef.current);
    }
  };

  const handleBpmChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 20 && value <= 300) {
      setBpm(value);
    }
  };

  const handleTimeSignatureChange = (e) => {
    const value = parseInt(e.target.value);
    setTimeSignature(value);
    beatCountRef.current = 0;
  };

  const incrementBpm = (amount) => {
    const newBpm = Math.min(300, Math.max(20, bpm + amount));
    setBpm(newBpm);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-black flex items-center justify-center p-4">
      <a href="/tools.html" className="absolute top-6 left-6 text-purple-400 hover:text-purple-300 transition flex items-center gap-2">
        <span className="text-2xl">←</span>
        <span className="hidden sm:inline">Back to Tools</span>
      </a>
      <div className="w-full max-w-md">
        <div className="bg-gray-900 rounded-3xl shadow-2xl p-8 border border-purple-500">
          {/* Title */}
          <h1 className="text-4xl font-bold text-center text-white mb-8">Metronome</h1>

          {/* BPM Display */}
          <div className={`bg-black rounded-2xl p-8 mb-8 border-4 transition-colors ${beatCount % 2 === 1 ? 'border-green-400 shadow-lg shadow-green-500' : 'border-purple-500'}`} style={{transitionDuration: `${Math.max(50, (60000 / bpm / 2) - 50)}ms`}}>
            <div className="text-center">
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Tempo (BPM)</p>
              <p className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                {bpm}
              </p>
            </div>
          </div>

          {/* BPM Controls */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => incrementBpm(-10)}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg transition"
            >
              -10
            </button>
            <button
              onClick={() => incrementBpm(-1)}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg transition"
            >
              -1
            </button>
            <button
              onClick={() => incrementBpm(1)}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg transition"
            >
              +1
            </button>
            <button
              onClick={() => incrementBpm(10)}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg transition"
            >
              +10
            </button>
          </div>

          {/* BPM Slider */}
          <div className="mb-8">
            <input
              type="range"
              min="20"
              max="300"
              value={bpm}
              onChange={handleBpmChange}
              className="w-full h-3 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>20</span>
              <span>300</span>
            </div>
          </div>

          {/* Time Signature */}
          <div className="mb-8">
            <label className="block text-gray-400 text-sm uppercase tracking-wider mb-3">
              Time Signature
            </label>
            <select
              value={timeSignature}
              onChange={handleTimeSignatureChange}
              className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg py-2 px-4 focus:border-purple-500 focus:outline-none"
            >
              <option value="2">2/4</option>
              <option value="3">3/4</option>
              <option value="4">4/4</option>
              <option value="6">6/8</option>
            </select>
          </div>

          {/* Play/Pause Button */}
          <button
            onClick={isPlaying ? handleStop : handleStart}
            className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition transform hover:scale-105 mb-4 flex items-center justify-center gap-2 ${
              isPlaying
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
            }`}
          >
            {isPlaying ? (
              <>
                <Pause size={24} /> Stop
              </>
            ) : (
              <>
                <Play size={24} /> Start
              </>
            )}
          </button>

          {/* Mute Button */}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-xl transition flex items-center justify-center gap-2"
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            {isMuted ? 'Unmute' : 'Mute'}
          </button>

          {/* Status Indicator */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              {isPlaying ? 'Playing' : 'Stopped'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
